import {
    Box,
    Divider,
    Pagination,
    Stack,
    Typography,
    useTheme,
  } from '@mui/material';
  import { generateClient } from 'aws-amplify/api';
import React, { useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { Schema } from '../../../../amplify/data/resource';
import ReportsList from './ReportsList/ReportsList';
import awsConfig from '../../../amplify_outputs.json';
  
  const client = generateClient<Schema>();
  
  interface Report {
    id: string;
    fileName: string;
    fullPath: string;
    fileExtension: string;
    type: 'INPUT' | 'OUTPUT';
    status: string;
    date: string;
    senderName: string;
    fileType: string; // EMPFED/FEDBKA for INPUT files
    processed: boolean;
    createdAt: string;
    updatedAt: string;
    executionDate?: string;
    // Fields for compatibility
    lastModified: string;
    fileSize: number;
    eTag: string;
    storageClass: string;
  }
  
  interface Folder {
    folderName: string;
    fullPath: string;
    type: string;
    fileCount?: number;
  }
  
  const Reports: React.FC = () => {
    const theme = useTheme();
  
    const [reports, setReports] = useState<Report[]>([]);
    const [folders, setFolders] = useState<Folder[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const limit = 50;

    const fetchReports = async (page: number = 1) => {
      const startTime = Date.now();
      setLoading(true);
      setError(null);
      
      // Convert to 0-based index for backend
      const pageIndex = page - 1;
      
      console.log('🚀 fetchReports START:', {
        requestedPage: page,
        pageIndex,
        limit,
        timestamp: new Date().toISOString()
      });
      
      try {
        const response = await client.queries.listReports({
          maxItems: limit,
          continuationToken: pageIndex.toString(),
        });

        console.log('📡 Raw GraphQL response:', {
          hasData: !!response.data,
          dataType: typeof response.data,
          hasExtensions: !!response.extensions,
          hasErrors: !!(response as any).errors
        });

        if (response.data && typeof response.data === 'string') {
          const parsedResponse = JSON.parse(response.data);
          console.log('📦 Parsed response:', {
            statusCode: parsedResponse.statusCode,
            hasBody: !!parsedResponse.body,
            bodyKeys: parsedResponse.body ? Object.keys(parsedResponse.body) : []
          });
          
          if (parsedResponse.statusCode === 200) {
            const body = parsedResponse.body;
            const pagination = body.pagination || {};
            
            console.log('✅ SUCCESS - Response data:', {
              filesCount: body.files?.length || 0,
              foldersCount: body.folders?.length || 0,
              totalCount: pagination.totalCount,
              currentPage: pagination.currentPage,
              totalPages: pagination.totalPages,
              firstFile: body.files?.[0]?.fileName,
              lastFile: body.files?.[body.files?.length - 1]?.fileName,
              debug: pagination.debug
            });
            
            // DO NOT SORT! Files are already sorted on backend by date
            setReports(body.files || []);
            setFolders(body.folders || []);
            setTotalPages(pagination.totalPages || 1);
            setTotalCount(pagination.totalCount || 0);
            
          } else {
            console.error('❌ Bad status code:', parsedResponse.statusCode);
            setError(`Server error: ${parsedResponse.statusCode}`);
          }
        } else {
          console.error('❌ Invalid response format:', {
            dataType: typeof response.data,
            data: response.data
          });
          setError('Invalid server response format');
        }
        
      } catch (error) {
        console.error('💥 fetchReports ERROR:', error);
        setError('Error loading reports');
      } finally {
        const duration = Date.now() - startTime;
        setLoading(false);
        console.log(`⏱️ fetchReports COMPLETE: ${duration}ms`);
      }
    };
  
    useEffect(() => {
      console.log(`🔄 useEffect triggered: currentPage=${currentPage}`);
      fetchReports(currentPage);
    }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps
  
    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
      console.log(`📄 Page change: ${currentPage} → ${page}`);
      if (page !== currentPage) {
        setCurrentPage(page);
      }
    };
  
    console.log('🎨 Component render:', {
      loading,
      error,
      reportsLength: reports.length,
      foldersLength: folders.length,
      currentPage,
      totalPages,
      totalCount,
    });

    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {/* Space for add report button if needed */}
          </Box>
          <Typography
            fontSize="30px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
            textAlign={'right'}
            sx={{ whiteSpace: 'nowrap' }}
          >
            כל הקבצים
          </Typography>
        </Box>
        <Divider
          sx={{
            width: '100%',
            height: '1px',
            backgroundColor: theme.palette.dark.border,
          }}
        />

        {error && (
          <Box sx={{ textAlign: 'center', margin: '20px 0' }}>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.error.main,
                fontFamily: 'AlmoniDL',
              }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ mt: '100px', display: 'flex', justifyContent: 'center' }}>
            <ReactLoading
              type={'bubbles'}
              color={theme.palette.green.main}
              height={250}
              width={250}
            />
          </Box>
        ) : reports.length > 0 || folders.length > 0 ? (
          <>
            <ReportsList
              reports={reports}
              folders={folders}
            />
            
            {/* Pagination info */}
            {totalCount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                {totalPages > 1 && (
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    shape="rounded"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#000000', // Black color for numbers
                        '&:hover': {
                          backgroundColor: 'rgba(186, 255, 42, 0.3)', // BAFF2A with transparency on hover
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#BAFF2A', // BAFF2A background for selected page
                          color: '#000000', // Black text on light background
                          '&:hover': {
                            backgroundColor: '#A5E820', // Slightly darker on hover for selected
                          },
                        },
                      },
                    }}
                  />
                )}
                
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                    fontFamily: 'AlmoniDL',
                    color: theme.palette.text.secondary,
                  }}
                >
                  עמוד {currentPage} מתוך {totalPages} • סה"כ {totalCount} קבצים
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              marginTop: '20px',
              fontFamily: 'AlmoniDL',
            }}
          >
            אין דוחות
          </Typography>
        )}
      </Box>
    );
  };
  
  export default Reports;
  